import type { ReactNode } from "react"
import { createPortal } from "react-dom"
import { VoiceDictationButton } from "@/components/voice/voice-dictation-button"
import { PinnedGenerationRow } from "@/components/workspace/pinned-tab-layers"
import { cn } from "@/lib/utils"

/** Колонка агента: промпт на всю высоту верхней зоны, генерация — в нижнем блоке. */
export type FlowPromptChrome = {
  fill?: boolean
  footerHost?: HTMLElement | null
}

type Props = {
  projectId: number
  /** Классы обёртки редактора (к базовому `relative`). */
  className?: string
  editor: ReactNode
  /** Диктовка (и generate без footerHost) в правом нижнем углу редактора; без canEdit не рисуются. */
  canEdit: boolean
  dictationDisabled: boolean
  onDictation: (text: string) => void
  generate: ReactNode
  /** Классы строки атрибутов генерации (к базовым). */
  attributesClassName?: string
  attributes: ReactNode
  /** Хвост строки атрибутов (стили проекта): в нижнем блоке колонки держится рядом с generate. */
  attributesTrailing?: ReactNode
  /**
   * Хост внутри рамки нижнего блока колонки (pinnedFooter): атрибуты и generate уходят туда
   * порталом, в редакторе остаётся только диктовка. Рамка принадлежит колонке.
   */
  footerHost?: HTMLElement | null
}

/** Каркас промпта ноды флоу: редактор с диктовкой, generate и строка атрибутов — под ним или в нижнем блоке. */
export function FlowPromptComposer({
  projectId,
  className,
  editor,
  canEdit,
  dictationDisabled,
  onDictation,
  generate,
  attributesClassName,
  attributes,
  attributesTrailing,
  footerHost = null,
}: Props) {
  return (
    <>
      <div className={cn("relative", className)}>
        {editor}
        {canEdit ? (
          <div className="absolute right-1 bottom-1 z-10 flex items-center gap-3">
            <VoiceDictationButton
              projectId={projectId}
              agentScope={{ kind: "project", projectId }}
              disabled={dictationDisabled}
              onText={onDictation}
            />
            {footerHost ? null : generate}
          </div>
        ) : null}
      </div>
      {footerHost ? (
        createPortal(
          <PinnedGenerationRow
            trailing={
              <>
                {attributesTrailing}
                {canEdit ? generate : null}
              </>
            }
          >
            {attributes}
          </PinnedGenerationRow>,
          footerHost,
        )
      ) : (
        <div
          className={cn(
            "relative z-10 flex min-w-0 flex-wrap items-center gap-2 overflow-visible",
            attributesClassName,
          )}
        >
          {attributes}
          {attributesTrailing}
        </div>
      )}
    </>
  )
}
